import { StoreState } from "hooks/use-store";
import { Captain } from "types/captains";
import { SystemStatus, SystemStatusChanges } from "types/system-status";
import { formatDuration, roundNumber } from "utils/formatters";

export enum RequestTopics {
  Alerts = "frontend/requests/alerts",
  Config = "frontend/requests/config",
  SystemStatus = "frontend/requests/system_status",
  StopCurrentTrip = "frontend/requests/stop_trip",
  StartNewTrip = "frontend/requests/new_trip",
  TransferData = "frontend/requests/usb_transfer",
  ThresholdConfig = "frontend/requests/threshold_config",
  TripCaptain = "frontend/requests/trip_captain",
  LocalStorage = "frontend/requests/local_storage",
  AcknowledgeAlert = "frontend/requests/acknowledge_alert",
  RebootDevice = "frontend/requests/reboot_device",
}

export enum Topics {
  TripId = "trip_id",
  HelmStatus = "helm_status",
  Sog = "frontend/sog",
  Rog = "frontend/rog",
  Rpm = "frontend/rpm",
  Impact = "frontend/impact",
  RedImpacts = "frontend/red_impacts",
  PeakImpact = "frontend/peak_impact",
  Duration = "frontend/duration",
  Distance = "frontend/distance",
  PairingRequest = "frontend/pairing_request",
  PairingCode = "frontend/pairing_code",
  SafetyQuality = "frontend/safety_quality",
  Alert = "frontend/alerts",
  SystemStatus = "frontend/system_status",
  SystemStatusChanges = "frontend/system_status_changes",
  EcoSog = "eco_sog",
  HasEcoSog = "frontend/eco_sog_status",
  WeatherData = "weather_all",
  WholeBodyVibration = "whole_body_vibration",
  TripCaptain = "trip_captain",
  Position = "position",
  // Publish-only analytics topics for the embedded device to store. Kept outside
  // "frontend/#" so they don't echo back into the app's own subscription; the
  // device can pick both up with "helm_analytics/#".
  AnalyticsProfile = "helm_analytics/profile",
  AnalyticsUsage = "helm_analytics/usage",
}

export interface ITopicHandler {
  [key: string]: {
    format?: (value: string) => any;
    set: (store: StoreState, value: any) => void;
  };
}

// Empty payloads (e.g. "" or "null") mean the value is unavailable and
// should be stored as null so the UI can show a placeholder
const parseNullableFloat = (value: string) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const roundNullableNumber = (value: string, decimals = 0) => {
  return Number.isFinite(parseFloat(value))
    ? roundNumber(value, decimals)
    : null;
};

export const TopicHandler: ITopicHandler = {
  [Topics.TripId]: {
    format: (value: string) => value,
    set: (store: StoreState, value: string) => store.setTripId(value),
  },
  [Topics.Sog]: {
    format: parseNullableFloat,
    set: (store: StoreState, value: number | null) => store.setSog(value),
  },

  [Topics.Rog]: {
    format: roundNullableNumber,
    set: (store: StoreState, value: number | null) => {
      store.setRog(value);
    },
  },

  [Topics.Rpm]: {
    format: parseNullableFloat,
    set: (store: StoreState, value: number | null) => {
      store.setRpm(value);
    },
  },

  [Topics.Impact]: {
    format: (value: string) => roundNullableNumber(value, 1),
    set: (store: StoreState, value: number | null) => store.setImpact(value),
  },

  [Topics.RedImpacts]: {
    format: roundNumber,
    set: (store: StoreState, value: number) => store.setRedImpacts(value),
  },

  [Topics.PeakImpact]: {
    format: (value: string) => roundNumber(value, 1),
    set: (store: StoreState, value: number) => store.setPeakImpact(value),
  },

  [Topics.Duration]: {
    format: formatDuration,
    set: (store: StoreState, value: string) => store.setDuration(value),
  },

  [Topics.Distance]: {
    format: roundNumber,
    set: (store: StoreState, value: string) => store.setDistance(value),
  },

  [Topics.PairingRequest]: {
    format: (value: string) => value === "true",
    set: (store: StoreState, value: boolean) => store.setPairingRequest(value),
  },

  [Topics.PairingCode]: {
    set: (store: StoreState, value: string) => store.setPairingCode(value),
  },

  [Topics.SafetyQuality]: {
    format: parseNullableFloat,
    set: (store: StoreState, value: number | null) =>
      store.setSafetyQuality(value),
  },

  [Topics.SystemStatus]: {
    format: JSON.parse,
    set: (store: StoreState, value: SystemStatus) =>
      store.setSystemStatus(value),
  },

  [Topics.SystemStatusChanges]: {
    format: JSON.parse,
    set: (store: StoreState, value: SystemStatusChanges) =>
      store.setSystemStatusChanges(value),
  },

  [Topics.Alert]: {
    format: JSON.parse,
    set: (store: StoreState, value: any) => {
      store.setAlert(value);
      store.increaseAlertCounter();
    },
  },

  [Topics.EcoSog]: {
    format: JSON.parse,
    set: (store: StoreState, value: any) => {
      store.setEcoSog(value);
    },
  },

  [Topics.HasEcoSog]: {
    format: (value: string) => JSON.parse(value),
    set: (store: StoreState, value: { enabled: boolean }) =>
      store.setHasEcoSog(value.enabled),
  },

  [Topics.WeatherData]: {
    format: JSON.parse,
    set: (store: StoreState, value: any) => store.setWeatherData(value),
  },

  [Topics.WholeBodyVibration]: {
    format: JSON.parse,
    set: (store: StoreState, value: any) => store.setWholeBodyVibration(value),
  },

  [Topics.TripCaptain]: {
    format: JSON.parse,
    set: (store: StoreState, value: Captain) => store.setTripCaptain(value),
  },

  [Topics.Position]: {
    // The payload is unused — receiving any position message means the GPS
    // is delivering data, so only the arrival time is recorded
    set: (store: StoreState) => store.setLastPositionAt(Date.now()),
  },
};
